import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const LessonInfo = ({ 
  lesson, 
  progress, 
  isBookmarked, 
  onToggleBookmark, 
  onShare 
}) => {
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [activeObjective, setActiveObjective] = useState(null);

  const truncatedDescription = lesson?.description?.substring(0, 200) + '...';
  const shouldTruncate = lesson?.description?.length > 200;

  const handleObjectiveToggle = (index) => {
    setActiveObjective(activeObjective === index ? null : index);
  };

  return (
    <div className="bg-card rounded-lg shadow-tier-1">
      {/* Action Bar */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center space-x-4">
          <Button
            variant={isBookmarked ? "default" : "outline"}
            onClick={onToggleBookmark}
            iconName="Bookmark"
            iconPosition="left"
            iconSize={18}
          >
            {isBookmarked ? 'Favoritado' : 'Favoritar'}
          </Button>
          
          <Button
            variant="outline"
            onClick={onShare}
            iconName="Share"
            iconPosition="left"
            iconSize={18}
          >
            Compartilhar
          </Button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="text-sm text-muted-foreground">
            Progresso da lição:
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-state"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-sm font-medium text-foreground">
              {Math.round(progress)}%
            </span>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">
          Sobre esta lição
        </h3>
        <div className="prose prose-lg max-w-none">
          <p className="text-muted-foreground leading-relaxed">
            {showFullDescription || !shouldTruncate ? lesson?.description : truncatedDescription}
          </p>
          {shouldTruncate && (
            <button
              onClick={() => setShowFullDescription(!showFullDescription)}
              className="text-primary hover:text-primary/80 font-medium text-sm mt-2 transition-micro"
            >
              {showFullDescription ? 'Ver menos' : 'Ver mais'}
            </button>
          )}
        </div>
      </div>

      {/* Learning Objectives */}
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">
          Objetivos de Aprendizagem
        </h3>
        <div className="space-y-3">
          {lesson?.objectives?.map((objective, index) => (
            <div
              key={index}
              className={`flex items-start space-x-3 p-3 rounded-lg transition-micro cursor-pointer ${
                activeObjective === index 
                  ? 'bg-primary/5 border border-primary/20' :'hover:bg-muted/50'
              }`}
              onClick={() => handleObjectiveToggle(index)}
            >
              <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${
                activeObjective === index 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-muted text-muted-foreground'
              }`}>
                <Icon 
                  name={activeObjective === index ? "Check" : "Target"} 
                  size={14} 
                />
              </div>
              <span className="text-sm text-card-foreground flex-1">
                {objective}
              </span>
              <Icon 
                name={activeObjective === index ? "ChevronUp" : "ChevronDown"} 
                size={16} 
                className="text-muted-foreground flex-shrink-0"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">
          Tópicos Abordados
        </h3>
        <div className="flex flex-wrap gap-2">
          {lesson?.tags?.map((tag, index) => (
            <span
              key={index}
              className="px-3 py-1 bg-secondary/10 text-secondary text-sm rounded-full font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Instructor Info */}
      <div className="p-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">
          Instrutor
        </h3>
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
            <AppImage
              src={lesson?.instructor?.avatar}
              alt={lesson?.instructor?.name}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex-1">
            <h4 className="font-heading font-medium text-foreground">
              {lesson?.instructor?.name}
            </h4>
            <p className="text-sm text-secondary mb-2">
              {lesson?.instructor?.title}
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {lesson?.instructor?.bio}
            </p>
          </div>
        </div>
      </div>

      {/* Call to Action - Dai Mentor */}
      <div className="p-6 bg-secondary/5 rounded-b-lg border-t border-border">
        <div className="flex items-start space-x-4">
          <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
            <Icon name="MessageSquare" size={20} color="var(--color-secondary-foreground)" />
          </div>
          <div className="flex-1">
            <h4 className="font-heading font-medium text-foreground mb-1">
              Dúvidas sobre a lição?
            </h4>
            <p className="text-sm text-muted-foreground mb-3">
              Converse com a Dai, nossa mentora IA, para esclarecer conceitos e aprofundar seu aprendizado.
            </p>
            <Button
              variant="secondary"
              size="sm"
              iconName="MessageSquare"
              iconPosition="left"
              iconSize={16}
              onClick={() => {
                // This would trigger the floating chat to open with lesson context
                const event = new CustomEvent('open-ai-chat', { 
                  detail: { 
                    context: `Estou estudando a lição "${lesson?.title}" sobre ${lesson?.tags?.join(', ')}.` 
                  } 
                });
                window.dispatchEvent(event);
              }}
            >
              Falar com a Dai
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonInfo;